import React, { useState } from 'react';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useLanguage } from '../../../../context/LanguageContext';
import { ChannelType, EntityTimeslot } from '../types';

interface TimeslotCellProps {
    entityId: number;
    channel: ChannelType;
    timeslot: EntityTimeslot;
    onSave: (entityId: number, channel: ChannelType, timeslotId: number | null, startHour: string, endHour: string) => Promise<boolean>;
    onRequestReset: (entityId: number, channel: ChannelType, timeslotId: number) => void;
}

const TimeslotCell = ({ entityId, channel, timeslot, onSave, onRequestReset }: TimeslotCellProps) => {
    const { t } = useLanguage();
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [startDraft, setStartDraft] = useState(timeslot.start_hour);
    const [endDraft, setEndDraft] = useState(timeslot.end_hour);

    const handleEdit = () => {
        setStartDraft(timeslot.start_hour);
        setEndDraft(timeslot.end_hour);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (startDraft >= endDraft) {
            return;
        }
        setSaving(true);
        const success = await onSave(entityId, channel, timeslot.timeslot_id, startDraft, endDraft);
        setSaving(false);
        if (success) {
            setIsEditing(false);
        }
    };

    if (isEditing) {
        const isInvalid = startDraft >= endDraft;
        return (
            <Box display="flex" alignItems="center" gap={0.5}>
                <TextField
                    type="time"
                    size="small"
                    value={startDraft}
                    onChange={e => setStartDraft(e.target.value)}
                    inputProps={{ step: 1 }}
                    error={isInvalid}
                    sx={{ width: 130 }}
                />
                <Typography variant="body2">-</Typography>
                <TextField
                    type="time"
                    size="small"
                    value={endDraft}
                    onChange={e => setEndDraft(e.target.value)}
                    inputProps={{ step: 1 }}
                    error={isInvalid}
                    sx={{ width: 130 }}
                />
                <Tooltip title={t('common.save')}>
                    <span>
                        <IconButton size="small" color="success" onClick={handleSave} disabled={saving || isInvalid}>
                            <CheckIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('common.cancel')}>
                    <span>
                        <IconButton size="small" onClick={handleCancel} disabled={saving}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );
    }

    return (
        <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" color={timeslot.timeslot_id != null ? 'text.primary' : 'text.secondary'}>
                {timeslot.start_hour} - {timeslot.end_hour}
            </Typography>
            <Tooltip title={t('common.edit')}>
                <IconButton size="small" color="primary" onClick={handleEdit}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            {timeslot.timeslot_id != null && (
                <Tooltip title={t('pages.marketing.timeslotsPage.resetToDefault')}>
                    <IconButton
                        size="small"
                        color="warning"
                        onClick={() => onRequestReset(entityId, channel, timeslot.timeslot_id as number)}
                    >
                        <RestartAltIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default TimeslotCell;
